import React, {useEffect, useState, useTransition} from 'react';
import Editor from "../../shared/components/editor/Editor";
import {Button, Input, Popconfirm, Splitter} from "antd";
import UploadComponent from "../../shared/components/upload/UploadComponent";
import {useLocation} from "react-router-dom";
import {
    useGetNewProbabilityDataBySeq,
    useSaveNewProbabilityData
} from "./useProbabilityData";
import {useCountryData} from "../country/useCountryData";
import IsLoading from "../../shared/components/IsLoading";
import ErrorPage from "../../shared/components/ErrorPage";
import {ProbabilityEntity} from "./ProbabilityEntity";

const NewProbabilityEditor = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const queryIndex = queryParams.get('index');

    const { data: countryData, isLoading: isCountryLoading, error: countryError } = useCountryData();
    const { data: probabilityData, isLoading: isProbLoading, error: probError } = useGetNewProbabilityDataBySeq(queryIndex);

    // 새 항목 생성 시 (queryIndex 없음): 로딩 체크 안함, 빈 Entity 사용
    // 기존 항목 편집 시 (queryIndex 있음): 로딩 완료까지 대기
    if (isCountryLoading || (queryIndex && isProbLoading)) return <IsLoading />;
    const firstError = countryError || probError;
    if (firstError) return <ErrorPage error={firstError} />;

    // queryIndex가 없으면 빈 ProbabilityEntity 사용
    const finalProbabilityData = queryIndex ? probabilityData : new ProbabilityEntity();

    return (
        <NewProbabilityEditorContent
            countryData={countryData}
            probabilityData={finalProbabilityData}
            queryIndex={queryIndex}
        />
    );
};

const NewProbabilityEditorContent = ({ countryData, probabilityData, queryIndex }) => {
    const { mutate: saveProbability } = useSaveNewProbabilityData();
    const [uniqueLangCode] = useState([...new Set(countryData.map((row) => row.langCode))]);
    const [currentLangCode, setCurrentLangCode] = useState(uniqueLangCode[0]);
    const [dataList, setDataList] = useState(uniqueLangCode.map(langCode => {
        if (queryIndex) {
            return {
                langCode: langCode,
                title: probabilityData.data[langCode]?.title,
                data: probabilityData.data[langCode]?.data
            }
        } else {
            return {
                langCode: langCode,
                title: '',
                data: ''
            }
        }
    }));
    const [currentData, setCurrentData] = useState(dataList.find(data => data.langCode === currentLangCode).data);
    const [inPending, startTransition] = useTransition();
    const [titleImage, setTitleImage] = useState(() => {
        if (queryIndex && probabilityData.data.titleImage) {
            return {
                uid: '-1',
                name: '타이틀 이미지',
                status: 'done',
                url: probabilityData.data.titleImage,
                response: {
                    url: probabilityData.data.titleImage
                }
            }
        } else {
            return {};
        }
    });
    const [description, setDescription] = useState('저장 하시겠습니까?');
    const [isOk, setIsOk] = useState(false);
    const [index] = useState(queryIndex || probabilityData?.seq || 0);

    useEffect(() => {
        const currentData = dataList.find(data => data.langCode === currentLangCode).data;
        startTransition(async () => setCurrentData(currentData));
    }, [currentLangCode]);

    const handleSetData = (newData) => {
        setDataList((prevDataList) => {
            return prevDataList.map((item) => {
                if (item.langCode === currentLangCode) {
                    return {
                        ...item,
                        data: newData
                    };
                }
                return item;
            });
        });
    }

    const handleSetTitle = (title) => {
        setDataList((prevDataList) => {
            return prevDataList.map((item) => {
                if (item.langCode === currentLangCode) {
                    return {
                        ...item,
                        title: title
                    };
                }
                return item;
            });
        });
    }

    const handleButtonClick = (langCode) => {
        setCurrentLangCode(langCode);
    };

    const handleOk = async e => {
        await handleSave();
    };

    const beforeSave = () => {
        const emptyList = [];
        setIsOk(true);
        dataList.forEach(item => {
            if (!item.data || !item.title) {
                emptyList.push(item.langCode);
            }
        });

        if (emptyList.length > 0) {
            if (emptyList.length === uniqueLangCode.length) {
                setDescription('저장 할 내용이 없습니다.');
                setIsOk(false);
            } else {
                const emptyStrings = emptyList.map(langCode => {
                    const languageName = countryData.find(e => e.langCode === langCode).languageName;
                    return `${languageName}`;
                });
                setDescription(`다음 언어에 대한 내용이 없습니다: [${emptyStrings.join(', ')}] 그래도 저장 하시겠습니까?`);
            }
        } else if (titleImage?.status !== 'done') {
            setDescription('타이틀 이미지를 등록해주세요.');
            setIsOk(false);
        } else {
            setDescription('저장 하시겠습니까?');
        }
    }

    const handleSave = async () => {
        const saveData = dataList.reduce((acc, item) => {
            if (!item.data || !item.title) {
                return acc;
            }

            acc[item.langCode] = {
                title: item.title,
                data: item.data
            };
            return acc;
        }, {});

        const updatedData = {
            [index]: {...saveData, titleImage: titleImage?.status === 'done' ? titleImage.response.url : ''},
        };

        // await handleUpload(updatedData);
        await handleUpload({...saveData, titleImage: titleImage?.status === 'done' ? titleImage.response.url : ''});
    }

    const handleUpload = async (updatedData) => {
        saveProbability({seq: probabilityData.seq, body: updatedData, onOff: probabilityData.onOff});
    }

    const stripHtmlTags = (str) => {
        if (!str) return '';
        return str
            .replace(/<[^>]*>/g, '')    // HTML 태그 제거
            .replace(/&nbsp;/gi, ' ')   // &nbsp;를 일반 공백으로 변경
            .replace(/&[a-z]+;/gi, '')  // 그 외 HTML 엔티티 제거 (옵션)
            .trim();
    }

    const lengthCheck = (langCode) => {
        return (stripHtmlTags(dataList.find(data => data.langCode === langCode).data).length > 0) && (dataList.find(data => data.langCode === langCode).title.length > 0);
    }

    const sampleHtml = () => {
        const current = dataList.find(e => e.langCode === currentLangCode)
        let header = '';

        if (titleImage?.status === 'done') {
            header = `<div style="text-align: center;">
                            <h1 style="display: inline-flex; align-items: center; gap: 8px; margin: 0;">
                                <img src="${titleImage.response.url}" alt="titleImage" width="41" height="41"/>
                                ${current.title}
                            </h1>
                       </div>`;
        } else {
            header = `<div style="text-align: center;">
                        <h1 style="display: inline-flex; align-items: center; gap: 8px; margin: 0;">${current.title}</>
                      </div>`;
        }
        return header + current.data;
    }

    return (
        <div>
            <Splitter style={{height: 1000, boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)', padding: 10}}>
                <Splitter.Panel defaultSize="50%" min="20%" max="70%">
                    <div>
                        {uniqueLangCode.map((langCode) => (
                            <Button
                                key={langCode}
                                onClick={() => handleButtonClick(langCode)}
                                type={currentLangCode === langCode ? 'primary' : 'dashed'}
                                variant={currentLangCode === langCode ? 'solid' : 'outlined'}
                                style={{margin: 5}}
                            >
                                {countryData.find(e => e.langCode === langCode).languageName} {lengthCheck(langCode) ? '✔' : ''}
                            </Button>
                        ))}
                    </div>
                    <div>
                        <div style={{display: 'flex', flexDirection: 'column', margin: 5}}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 10,
                                marginBottom: 10,
                                flexWrap: 'no-wrap'
                            }}>
                                <UploadComponent image={titleImage} setImage={setTitleImage}
                                                 name={titleImage?.status === 'done' ? '이미지 변경' : '타이틀 이미지'}/>
                                <Input
                                    placeholder="타이틀"
                                    value={dataList.find(data => data.langCode === currentLangCode).title}
                                    onChange={(e) => handleSetTitle(e.target.value)}
                                    size={"large"}
                                    style={{width: 'auto', flexGrow: 1}}
                                />
                            </div>
                            {!inPending && (
                                <Editor
                                    setData={handleSetData}
                                    data={currentData}
                                    uploadUrl={'upload/probability/images'}
                                />
                            )}
                        </div>
                    </div>
                </Splitter.Panel>
                <Splitter.Panel>
                    {!inPending && (
                        <div
                            className="ck ck-content ck-editor__editable ck-rounded-corners ck-editor__editable_inline ck-blurred"
                            dangerouslySetInnerHTML={{__html: sampleHtml()}} // 결과 확인
                        />
                    )}
                </Splitter.Panel>
            </Splitter>
            <Popconfirm
                title="저장하기"
                description={description}
                onConfirm={isOk ? handleOk : () => {
                }}
                showCancel={isOk}
            >
                <Button onClick={beforeSave} style={{marginTop: 20}}>저장</Button>
            </Popconfirm>
        </div>
    );
};

export default NewProbabilityEditor;